<template>
    <inline-tab-panel name="flow">
        <flex-container>
            <flex-container justify-content="space-between" align-items="center">
                <flex-container
                    :gap="Size.sm"
                    align-items="center"
                    size="auto"
                >
                    <text-heading>
                        Flows
                    </text-heading>

                    <chip-informative :color="'primary'">
                        {{ props.leadCollection.total }}
                    </chip-informative>
                </flex-container>

                <flex-container size="auto">
                    <cancel-executions-for-contact-button
                        :contact-id="props.contactId"
                    />
                </flex-container>
            </flex-container>

            <card-normal without-padding>
                <list-container>
                    <template #header>
                        <list-cell-header>
                            <template #title>
                                Startdatum
                            </template>
                        </list-cell-header>
                        <list-cell-header>
                            <template #title>
                                Flow
                            </template>
                        </list-cell-header>
                        <list-cell-header>
                            <template #title>
                                Laatst ontvangen email
                            </template>
                        </list-cell-header>
                        <list-cell-header>
                            <template #title>
                                Status
                            </template>
                        </list-cell-header>
                        <list-cell-header>
                            <template #title>
                                Uitschrijven
                            </template>
                        </list-cell-header>
                    </template>

                    <template #content>
                        <list-row v-if="props.leadCollection.data.value.length === 0">
                            <list-cell-text colspan="6">
                                <template #content>
                                    Er zijn geen flows voor dit contact
                                </template>
                            </list-cell-text>
                        </list-row>
                        <list-row
                            v-for="(lead, index) in props.leadCollection.data.value"
                            :key="lead.id.value"
                            :background-color="index % 2 ? 'gray-100' : 'white'"
                        >
                            <list-cell-text>
                                <template #content>
                                    {{ formatDate(lead.createdAt) }}
                                </template>
                            </list-cell-text>
                            <list-cell-text>
                                <template #content>
                                    {{ lead.pipeline.name }}
                                </template>
                            </list-cell-text>
                            <list-cell-text>
                                <template
                                    v-if="lastSentEmailForLead(lead).value"
                                    #content
                                >
                                    {{ lastSentEmailForLead(lead).value?.email.subject }}
                                </template>
                                <template
                                    v-else
                                    #content
                                >
                                    <chip-informative>
                                        Geen email gevonden
                                    </chip-informative>
                                </template>
                            </list-cell-text>
                            <list-cell-text>
                                <template #content>
                                    <flex-container
                                        v-if="lead.status.equals(Status.OPEN)"
                                        :gap="Size.sm"
                                        align-items="center"
                                        no-wrap
                                    >
                                        <svg-icon
                                            :color="'gray-400'"
                                            :size="IconSize.sm"
                                            :icon="IconSandWalker"
                                            is-custom-icon
                                        />
                                        Lopend
                                    </flex-container>
                                    <flex-container
                                        v-if="lead.status.equals(Status.WON)"
                                        :gap="Size.sm"
                                        align-items="center"
                                        no-wrap
                                    >
                                        <svg-icon
                                            :icon="IconCheck"
                                            :size="IconSize.sm"
                                            :color="'green-500'"
                                            is-custom-icon
                                        />
                                        Voltooid
                                    </flex-container>
                                    <flex-container
                                        v-if="lead.status.equals(Status.LOST)"
                                        :gap="Size.sm"
                                        align-items="center"
                                        no-wrap
                                    >
                                        <svg-icon
                                            :icon="IconX"
                                            :size="IconSize.sm"
                                            :color="'red-500'"
                                            is-custom-icon
                                        />
                                        Uitgeschreven
                                    </flex-container>
                                </template>
                            </list-cell-text>
                            <list-cell-text>
                                <template #content>
                                    <lead-cancel-execution-button
                                        :lead="lead"
                                    />
                                </template>
                            </list-cell-text>
                        </list-row>
                    </template>
                </list-container>
            </card-normal>
        </flex-container>
    </inline-tab-panel>
</template>

<script setup lang="ts">
    import { computed, type ComputedRef } from 'vue';
    import type { ReactiveCollection } from '@/services/keyvue-api/composables/useClient';
    import { ContactId, Lead, SentFlowEmail, Status } from '@keyvue/one-api-client';
    import { IconSize, ListRow, Size } from '@keyvue/one-ui';
    import { formatDate } from '@/shared/utils/date';
    import IconX from '@keyvue/one-awesome-icons/icons/regular/x.svg?component';
    import IconSandWalker from '@keyvue/one-awesome-icons/icons/regular/hourglass.svg?component';
    import IconCheck from '@keyvue/one-awesome-icons/icons/regular/check.svg?component';
    import LeadCancelExecutionButton from '@/flow/execution/components/LeadCancelExecutionButton.vue';
    import CancelExecutionsForContactButton from '@/flow/execution/components/CancelExecutionsForContactButton.vue';

    interface Props {
        leadCollection: ReactiveCollection<Lead>
        sentEmailCollection: ReactiveCollection<SentFlowEmail>
        contactId: ContactId
    }

    const props = defineProps<Props>();

    const sentEmailsPerLead = computed(() => {
        const sentEmailsPerLeadObject: Record<string, SentFlowEmail[]> = {};

        props.sentEmailCollection.data.value.forEach((email) => {
            if (!sentEmailsPerLeadObject[email.lead.id.value]) {
                sentEmailsPerLeadObject[email.lead.id.value] = [];
            }

            sentEmailsPerLeadObject[email.lead.id.value].push(email);
        });

        const keys = Object.keys(sentEmailsPerLeadObject);

        keys.forEach((key) => {
            sentEmailsPerLeadObject[key] = sentEmailsPerLeadObject[key].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
        });

        return sentEmailsPerLeadObject;
    });

    function sentEmailsForLead(lead: Lead): ComputedRef<SentFlowEmail[]> {
        return computed(() => {
            if (lead.id.value in sentEmailsPerLead.value) {
                return sentEmailsPerLead.value[lead.id.value];
            }

            return [];
        });
    }

    function lastSentEmailForLead(lead: Lead): ComputedRef<SentFlowEmail | null> {
        return computed(() => {
            const sentEmails = sentEmailsForLead(lead);
            if (sentEmails.value.length > 0) {
                return sentEmails.value[0];
            }

            return null;
        });
    }
</script>
